import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  CreditCard, Plus, Pencil, Trash2, Loader2, FileSpreadsheet, Upload, Sparkles,
  CheckCircle2, Clock, Filter,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Travel', 'Meals', 'Software', 'Marketing', 'Office', 'Fees', 'Other'];
const CATEGORY_COLOR = {
  Travel: 'bg-sky-100 text-sky-800 border-sky-200',
  Meals: 'bg-amber-100 text-amber-800 border-amber-200',
  Software: 'bg-violet-100 text-violet-800 border-violet-200',
  Marketing: 'bg-pink-100 text-pink-800 border-pink-200',
  Office: 'bg-slate-100 text-slate-800 border-slate-200',
  Fees: 'bg-rose-100 text-rose-800 border-rose-200',
  Other: 'bg-gray-100 text-gray-800 border-gray-200',
};
const CARD_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#1f2937'];

const cad = (n) => `C$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function CreditCardsPanel() {
  const [cards, setCards] = useState([]);
  const [activeCardId, setActiveCardId] = useState('all');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');  // all | draft | confirmed
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Card modal
  const [editingCard, setEditingCard] = useState(null);
  const [savingCard, setSavingCard] = useState(false);

  // Expense modal
  const [editingExpense, setEditingExpense] = useState(null);
  const [savingExpense, setSavingExpense] = useState(false);

  // Statement scan
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef(null);
  const [scanResult, setScanResult] = useState(null);  // {statement, draft_count, drafts}

  // Load helpers
  const loadCards = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/finance/cc/cards`);
      setCards(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load cards');
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (activeCardId !== 'all') params.card_id = activeCardId;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/admin/finance/cc/expenses`, { params });
      setExpenses(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [activeCardId, statusFilter, categoryFilter, search]);

  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (activeCardId !== 'all') params.card_id = activeCardId;
      const { data } = await axios.get(`${API}/admin/finance/cc/summary`, { params });
      setSummary(data);
    } catch (e) {
      // Silent
    }
  }, [activeCardId]);

  useEffect(() => { loadCards(); }, [loadCards]);
  useEffect(() => { loadExpenses(); loadSummary(); }, [loadExpenses, loadSummary]);

  // -------- Card CRUD -------------------------------------------------
  const openNewCard = () => setEditingCard({ name: '', last4: '', color: CARD_COLORS[0], note: '' });
  const saveCard = async () => {
    if (!editingCard?.name?.trim()) { toast.error('Card name is required.'); return; }
    setSavingCard(true);
    try {
      const payload = {
        name: editingCard.name.trim(),
        last4: editingCard.last4 ? editingCard.last4.slice(-4) : null,
        color: editingCard.color || '#6366f1',
        note: editingCard.note || '',
      };
      if (editingCard.id) {
        await axios.patch(`${API}/admin/finance/cc/cards/${editingCard.id}`, payload);
        toast.success('Card updated');
      } else {
        await axios.post(`${API}/admin/finance/cc/cards`, payload);
        toast.success('Card added');
      }
      setEditingCard(null);
      await loadCards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingCard(false);
    }
  };

  const deleteCard = async (card) => {
    if (!window.confirm(`Delete card "${card.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/finance/cc/cards/${card.id}`);
      toast.success('Card removed');
      if (activeCardId === card.id) setActiveCardId('all');
      await loadCards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // -------- Expense CRUD ----------------------------------------------
  const openNewExpense = () => {
    if (cards.length === 0) {
      toast.error('Add a card first.');
      return;
    }
    setEditingExpense({
      card_id: activeCardId !== 'all' ? activeCardId : cards[0].id,
      date: todayISO(),
      merchant: '',
      amount: '',
      category: 'Other',
      note: '',
      status: 'confirmed',
    });
  };

  const openEditExpense = (row) => setEditingExpense({
    ...row,
    amount: String(row.amount ?? ''),
  });

  const saveExpense = async () => {
    if (!editingExpense) return;
    const amt = parseFloat(editingExpense.amount);
    if (!editingExpense.merchant?.trim()) { toast.error('Merchant is required.'); return; }
    if (!(amt > 0)) { toast.error('Amount must be greater than zero.'); return; }
    setSavingExpense(true);
    try {
      const payload = {
        card_id: editingExpense.card_id,
        date: editingExpense.date,
        merchant: editingExpense.merchant.trim(),
        amount: amt,
        category: editingExpense.category,
        note: editingExpense.note || null,
        status: editingExpense.status || 'confirmed',
      };
      if (editingExpense.id) {
        await axios.patch(`${API}/admin/finance/cc/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/admin/finance/cc/expenses`, payload);
        toast.success('Expense added');
      }
      setEditingExpense(null);
      await Promise.all([loadExpenses(), loadSummary(), loadCards()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingExpense(false);
    }
  };

  const deleteExpense = async (row) => {
    if (!window.confirm(`Delete expense "${row.merchant}" (${cad(row.amount)})?`)) return;
    try {
      await axios.delete(`${API}/admin/finance/cc/expenses/${row.id}`);
      toast.success('Expense deleted');
      await Promise.all([loadExpenses(), loadSummary(), loadCards()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const confirmExpense = async (row) => {
    try {
      await axios.patch(`${API}/admin/finance/cc/expenses/${row.id}`, { status: 'confirmed' });
      await Promise.all([loadExpenses(), loadSummary(), loadCards()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    }
  };

  // -------- Statement scan --------------------------------------------
  const handleScan = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (activeCardId === 'all') {
      toast.error('Pick a specific card tab before scanning a statement.');
      return;
    }
    setScanning(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await axios.post(
        `${API}/admin/finance/cc/statements/scan?card_id=${activeCardId}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
      setScanResult(data);
      toast.success(`AI extracted ${data.draft_count} transaction${data.draft_count === 1 ? '' : 's'} as drafts — review and confirm.`);
      await Promise.all([loadExpenses(), loadSummary()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Statement scan failed');
    } finally {
      setScanning(false);
    }
  };

  const confirmAllDraftsFromStatement = async () => {
    if (!scanResult?.statement?.id) return;
    try {
      const { data } = await axios.post(`${API}/admin/finance/cc/expenses/confirm`, {
        statement_id: scanResult.statement.id,
      });
      toast.success(`Confirmed ${data.confirmed} expense${data.confirmed === 1 ? '' : 's'}`);
      setScanResult(null);
      await Promise.all([loadExpenses(), loadSummary(), loadCards()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk confirm failed');
    }
  };

  const bulkConfirmFiltered = async () => {
    const draftIds = expenses.filter(x => x.status === 'draft').map(x => x.id);
    if (draftIds.length === 0) { toast.info('No drafts in the current view.'); return; }
    if (!window.confirm(`Confirm all ${draftIds.length} drafts in view?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/finance/cc/expenses/confirm`, {
        expense_ids: draftIds,
      });
      toast.success(`Confirmed ${data.confirmed}`);
      await Promise.all([loadExpenses(), loadSummary(), loadCards()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk confirm failed');
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (activeCardId !== 'all') params.card_id = activeCardId;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/finance/cc/export.xlsx`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit_card_expenses_${todayISO()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // -------- Render ----------------------------------------------------
  const totalInView = useMemo(() => expenses.reduce((a, r) => a + (r.amount || 0), 0), [expenses]);
  const draftCount = useMemo(() => expenses.filter(r => r.status === 'draft').length, [expenses]);

  return (
    <div className="space-y-6" data-testid="credit-cards-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Credit Cards</h1>
          <p className="text-sm text-gray-500 mt-1">Track card expenses. Upload a statement to let AI draft every row — confirm with one click.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={scanInputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            onChange={handleScan}
            className="hidden"
            data-testid="cc-scan-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => scanInputRef.current?.click()}
            disabled={scanning || activeCardId === 'all'}
            className="h-9 text-xs bg-gradient-to-r from-indigo-50 to-sky-50 border-indigo-200 text-indigo-800 hover:from-indigo-100 hover:to-sky-100"
            data-testid="cc-scan-btn"
          >
            {scanning ? <><Loader2 size={14} className="mr-1 animate-spin" /> Scanning statement…</> : <><Sparkles size={14} className="mr-1" /> Scan statement PDF</>}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} className="h-9 text-xs" data-testid="cc-export">
            <FileSpreadsheet size={14} className="mr-1" /> Excel
          </Button>
          <Button size="sm" onClick={openNewExpense} className="h-9 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="cc-new-expense">
            <Plus size={13} className="mr-1" /> New expense
          </Button>
        </div>
      </div>

      {/* Card tabs */}
      <div className="flex items-stretch gap-3 flex-wrap">
        <CardTab
          active={activeCardId === 'all'}
          onClick={() => setActiveCardId('all')}
          name="All cards"
          subtitle={`${cards.length} card${cards.length === 1 ? '' : 's'}`}
          color="#1f2937"
          testid="cc-tab-all"
        />
        {cards.map(c => (
          <CardTab
            key={c.id}
            active={activeCardId === c.id}
            onClick={() => setActiveCardId(c.id)}
            name={c.name}
            subtitle={c.last4 ? `**** ${c.last4}` : ' '}
            total={c.lifetime_spent}
            color={c.color}
            onEdit={() => setEditingCard({ ...c })}
            onDelete={() => deleteCard(c)}
            testid={`cc-tab-${c.id}`}
          />
        ))}
        <button
          onClick={openNewCard}
          className="min-w-[140px] px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-indigo-400 hover:text-indigo-700 flex items-center justify-center gap-1 text-sm"
          data-testid="cc-new-card-btn"
        >
          <Plus size={14} /> Add card
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total spent (confirmed)" value={summary ? cad(summary.total_confirmed) : '…'} subtle={summary ? `${summary.confirmed_count} transaction${summary.confirmed_count === 1 ? '' : 's'}` : ''} testid="cc-kpi-total" highlight />
        <Kpi label="Pending drafts" value={summary ? summary.pending_drafts : '…'} subtle="need admin review" testid="cc-kpi-drafts" alert={summary?.pending_drafts > 0} />
        <Kpi label="Top category" value={summary ? topCategory(summary.by_category) : '…'} subtle="by amount spent" testid="cc-kpi-topcat" />
        <Kpi label="In this view" value={cad(totalInView)} subtle={`${expenses.length} rows`} testid="cc-kpi-view" />
      </div>

      {/* Category breakdown */}
      {summary && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-3">By category (confirmed)</div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
            {CATEGORIES.map(c => (
              <div key={c} className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50/50">
                <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLOR[c]}`}>{c}</Badge>
                <div className="mt-1 font-mono text-sm font-semibold text-gray-800">{cad(summary.by_category?.[c] || 0)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-gray-400" />
        <Input
          placeholder="Search merchant or note"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-56 text-xs"
          data-testid="cc-search"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="cc-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="draft">Drafts only</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="cc-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {draftCount > 0 && (
          <Button size="sm" variant="outline" onClick={bulkConfirmFiltered} className="h-8 text-xs text-emerald-700 border-emerald-200" data-testid="cc-bulk-confirm">
            <CheckCircle2 size={12} className="mr-1" /> Confirm {draftCount} draft{draftCount === 1 ? '' : 's'}
          </Button>
        )}
      </div>

      {/* Expenses table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Card</th>
                <th className="px-4 py-3">Merchant</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                  <Loader2 size={14} className="inline mr-2 animate-spin" /> Loading…
                </td></tr>
              )}
              {!loading && expenses.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                  No expenses yet. {activeCardId !== 'all' && <>Click <strong>Scan statement PDF</strong> to let AI extract rows, or add one manually.</>}
                </td></tr>
              )}
              {!loading && expenses.map(r => {
                const card = cards.find(c => c.id === r.card_id);
                return (
                  <tr key={r.id} className={`border-b border-gray-100 hover:bg-gray-50/50 ${r.status === 'draft' ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{r.date}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: card?.color || '#6366f1' }} />
                        <div className="text-xs">
                          <div className="font-medium text-gray-800">{card?.name || '—'}</div>
                          {card?.last4 && <div className="text-[10px] text-gray-400">**** {card.last4}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">{r.merchant}</div>
                      {r.note && <div className="text-[11px] text-gray-500 mt-0.5">{r.note}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLOR[r.category] || CATEGORY_COLOR.Other}`}>{r.category}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">{cad(r.amount)}</td>
                    <td className="px-4 py-2.5">
                      {r.status === 'draft' ? (
                        <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">
                          <Clock size={10} className="mr-1" /> Draft
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">
                          <CheckCircle2 size={10} className="mr-1" /> Confirmed
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.status === 'draft' && (
                          <Button size="sm" variant="ghost" onClick={() => confirmExpense(r)} className="h-7 px-2 text-[11px] text-emerald-700" data-testid={`cc-confirm-${r.id}`}>
                            <CheckCircle2 size={12} className="mr-1" /> Confirm
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEditExpense(r)} className="h-7 w-7 p-0" data-testid={`cc-edit-${r.id}`}>
                          <Pencil size={13} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteExpense(r)} className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" data-testid={`cc-delete-${r.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {expenses.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-300 text-xs">
                  <td colSpan={4} className="px-4 py-2 font-semibold text-gray-700 uppercase tracking-wider">Totals ({expenses.length})</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-gray-900">{cad(totalInView)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Card modal */}
      {editingCard !== null && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) setEditingCard(null); }}>
          <DialogContent className="max-w-md" data-testid="cc-card-modal">
            <DialogHeader>
              <DialogTitle>{editingCard.id ? 'Edit card' : 'New card'}</DialogTitle>
              <DialogDescription className="text-xs">Give the card a name you recognise. Last-4 is optional but useful for statement scans.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">Card name *</Label>
                <Input value={editingCard.name} onChange={(e) => setEditingCard(c => ({ ...c, name: e.target.value }))} placeholder="e.g. RBC Visa Business" className="h-9 text-sm" data-testid="cc-card-name" />
              </div>
              <div>
                <Label className="text-xs">Last 4 digits</Label>
                <Input value={editingCard.last4 || ''} onChange={(e) => setEditingCard(c => ({ ...c, last4: e.target.value.replace(/\D/g, '').slice(-4) }))} placeholder="1234" maxLength={4} className="h-9 text-sm font-mono w-32" data-testid="cc-card-last4" />
              </div>
              <div>
                <Label className="text-xs">Colour</Label>
                <div className="flex gap-2 mt-1">
                  {CARD_COLORS.map(col => (
                    <button
                      key={col}
                      onClick={() => setEditingCard(c => ({ ...c, color: col }))}
                      className={`w-7 h-7 rounded-full border-2 ${editingCard.color === col ? 'border-gray-900' : 'border-white'} shadow`}
                      style={{ background: col }}
                      aria-label={`Choose colour ${col}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Textarea value={editingCard.note || ''} onChange={(e) => setEditingCard(c => ({ ...c, note: e.target.value }))} rows={2} className="text-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingCard(null)} disabled={savingCard}>Cancel</Button>
              <Button onClick={saveCard} disabled={savingCard} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="cc-card-save">
                {savingCard ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : (editingCard.id ? 'Save changes' : 'Add card')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Expense modal */}
      {editingExpense !== null && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) setEditingExpense(null); }}>
          <DialogContent className="max-w-xl" data-testid="cc-expense-modal">
            <DialogHeader>
              <DialogTitle>{editingExpense.id ? 'Edit expense' : 'New expense'}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs">Card *</Label>
                <Select value={editingExpense.card_id} onValueChange={(v) => setEditingExpense(x => ({ ...x, card_id: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="cc-expense-card"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cards.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.last4 ? ` **** ${c.last4}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={editingExpense.date} onChange={(e) => setEditingExpense(x => ({ ...x, date: e.target.value }))} className="h-9 text-sm" data-testid="cc-expense-date" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Merchant *</Label>
                <Input value={editingExpense.merchant} onChange={(e) => setEditingExpense(x => ({ ...x, merchant: e.target.value }))} className="h-9 text-sm" data-testid="cc-expense-merchant" />
              </div>
              <div>
                <Label className="text-xs">Amount (CAD) *</Label>
                <Input type="number" step="0.01" min="0" value={editingExpense.amount} onChange={(e) => setEditingExpense(x => ({ ...x, amount: e.target.value }))} className="h-9 text-sm font-mono" data-testid="cc-expense-amount" />
              </div>
              <div>
                <Label className="text-xs">Category *</Label>
                <Select value={editingExpense.category} onValueChange={(v) => setEditingExpense(x => ({ ...x, category: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="cc-expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Status</Label>
                <Select value={editingExpense.status} onValueChange={(v) => setEditingExpense(x => ({ ...x, status: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="cc-expense-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="draft">Draft (needs review)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Note</Label>
                <Textarea value={editingExpense.note || ''} onChange={(e) => setEditingExpense(x => ({ ...x, note: e.target.value }))} rows={2} className="text-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingExpense(null)} disabled={savingExpense}>Cancel</Button>
              <Button onClick={saveExpense} disabled={savingExpense} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="cc-expense-save">
                {savingExpense ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : (editingExpense.id ? 'Save changes' : 'Add expense')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Scan-result summary modal */}
      {scanResult && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) setScanResult(null); }}>
          <DialogContent className="max-w-md" data-testid="cc-scan-result-modal">
            <DialogHeader>
              <DialogTitle>Statement scanned</DialogTitle>
              <DialogDescription className="text-xs">
                <strong>{scanResult.statement?.filename || 'Statement'}</strong>
                {scanResult.statement?.period_start && <> · {scanResult.statement.period_start} → {scanResult.statement.period_end}</>}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 text-sm text-gray-700 space-y-2">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                AI pulled out <strong>{scanResult.draft_count}</strong> transaction{scanResult.draft_count === 1 ? '' : 's'} as drafts. They now show up in the table below with a <em>Draft</em> badge.
              </div>
              <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                <li>Review each row — edit merchant / category / amount if needed.</li>
                <li>Click <strong>Confirm all</strong> below to accept them in one click.</li>
                <li>Payments / credits / refunds are intentionally skipped.</li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setScanResult(null)}>Review manually</Button>
              <Button onClick={confirmAllDraftsFromStatement} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cc-confirm-all-from-statement">
                <CheckCircle2 size={14} className="mr-1" /> Confirm all {scanResult.draft_count}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CardTab({ active, onClick, name, subtitle, total, color, onEdit, onDelete, testid }) {
  return (
    <div className={`relative group min-w-[160px] px-4 py-3 rounded-xl border transition-all cursor-pointer ${
      active ? 'bg-white border-indigo-500 shadow-md' : 'bg-white border-gray-200 hover:border-gray-400'
    }`} onClick={onClick} data-testid={testid}>
      <div className="flex items-center gap-2">
        <CreditCard size={14} style={{ color }} />
        <div className="text-xs font-semibold text-gray-900 truncate">{name}</div>
      </div>
      <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{subtitle}</div>
      {typeof total === 'number' && (
        <div className="text-sm font-mono font-semibold text-gray-900 mt-1">{cad(total)}</div>
      )}
      {onEdit && onDelete && (
        <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="w-5 h-5 rounded hover:bg-gray-100 flex items-center justify-center" data-testid={`${testid}-edit`}>
            <Pencil size={10} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="w-5 h-5 rounded hover:bg-rose-100 text-rose-600 flex items-center justify-center" data-testid={`${testid}-delete`}>
            <Trash2 size={10} />
          </button>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, subtle, testid, highlight, alert }) {
  return (
    <div className={`p-4 rounded-xl border ${
      highlight ? 'bg-indigo-50 border-indigo-200' :
      alert ? 'bg-amber-50 border-amber-200' :
      'bg-white border-gray-200'
    }`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{label}</div>
      <div className={`mt-1.5 text-xl font-bold ${
        highlight ? 'text-indigo-900' : alert ? 'text-amber-900' : 'text-gray-900'
      }`} style={{ fontFamily: "'Libre Baskerville', serif" }}>
        {value}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{subtle}</div>
    </div>
  );
}

function topCategory(byCat) {
  if (!byCat) return '—';
  const sorted = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  const [name, total] = sorted[0] || ['—', 0];
  return total > 0 ? `${name} · ${cad(total)}` : '—';
}
